import { Link, useParams, useSearch } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { Badge } from "@/components/ui/badge";
import { carryFilters } from "@/lib/filters";

import { isKnownTab, tabFor } from "./routes";
import { useFilterOptions } from "./SharedFilterBar";

const AREA = "sourcing";
const MODULE = "sourcing_funnel";

/** A page left by a link: the page, and the item, variant and (on Option Overview) option it showed. */
export interface TrailStep {
  tab: string;
  item?: string;
  variant?: string;
  option?: string;
}

/** Older steps drop off the front, so a long walk keeps the last ten pages and a short URL. */
export const TRAIL_MAX = 10;

const id = (value: unknown): string | undefined =>
  typeof value === "string" && value !== "" ? value : undefined;

/** The trail in the URL. Any malformed step, or a page this module does not have, makes it no trail. */
export function readTrail(search: Record<string, unknown>): TrailStep[] {
  const raw = search.trail;
  if (!Array.isArray(raw)) return [];
  const steps: TrailStep[] = [];
  for (const entry of raw) {
    if (entry === null || typeof entry !== "object") return [];
    const fields = entry as Record<string, unknown>;
    if (!isKnownTab(fields.tab)) return [];
    const step: TrailStep = { tab: fields.tab };
    for (const key of ["item", "variant", "option"] as const) {
      const value = id(fields[key]);
      if (value) step[key] = value;
    }
    steps.push(step);
  }
  return steps.slice(-TRAIL_MAX);
}

/** The trail after leaving `from`: the step it adds holds what that page showed. An unknown page adds none. */
export function pushTrail(previous: Record<string, unknown>, from: unknown): TrailStep[] {
  const steps = readTrail(previous);
  if (!isKnownTab(from)) return steps;
  const step: TrailStep = { tab: from };
  const item = id(previous.f_item);
  const variant = id(previous.f_variant);
  const option = from === "option_overview" ? id(previous.option) ?? id(previous.f_option) : undefined;
  if (item) step.item = item;
  if (variant) step.variant = variant;
  if (option) step.option = option;
  return [...steps, step].slice(-TRAIL_MAX);
}

function withTrail(next: Record<string, unknown>, previous: Record<string, unknown>, from: unknown) {
  const trail = pushTrail(previous, from);
  if (trail.length > 0) next.trail = trail;
  else delete next.trail;
  return next;
}

/**
 * The search that returns to step `index` of the trail: that page's item, variant and option, the
 * other filters as they are now, and the trail cut back to the steps before it.
 */
export function trailBackSearch(previous: Record<string, unknown>, index: number): Record<string, unknown> {
  const steps = readTrail(previous);
  const step = steps[index];
  const next: Record<string, unknown> = carryFilters(previous);
  if (!step) return next;
  for (const [key, value] of [["f_item", step.item], ["f_variant", step.variant]] as const) {
    if (value) next[key] = value;
    else delete next[key];
  }
  if (step.item !== id(previous.f_item)) {
    delete next.f_option;
    delete next.f_line;
  }
  if (step.option) next.option = step.option;
  if (index > 0) next.trail = steps.slice(0, index);
  return next;
}

export function itemLinkSearch(
  previous: Record<string, unknown>,
  itemId: string,
  from: string,
): Record<string, unknown> {
  const next: Record<string, unknown> = { ...carryFilters(previous), f_item: itemId, from };
  if (previous.f_item !== itemId) {
    delete next.f_option;
    delete next.f_line; // Item Overview's cost line belongs to the item it was picked for
  }
  return withTrail(next, previous, from);
}

export function optionLinkSearch(
  previous: Record<string, unknown>,
  optionId: string,
  itemId: string,
  from: string,
): Record<string, unknown> {
  const next: Record<string, unknown> = {
    ...carryFilters(previous),
    f_item: itemId,
    option: optionId,
    from,
  };
  if (previous.f_item !== itemId) {
    delete next.f_option;
    delete next.f_line;
  }
  return withTrail(next, previous, from);
}

/** A link to another page of the module: the filters as they are, `search` may add to them, and the trail. */
export function tabLinkSearch(
  previous: Record<string, unknown>,
  from: unknown,
  search?: (previous: Record<string, unknown>) => Record<string, unknown>,
): Record<string, unknown> {
  return withTrail(search ? search(previous) : carryFilters(previous), previous, from);
}

function useRoute() {
  const params = useParams({ strict: false }) as { area?: string; module?: string; tab?: string };
  return { area: params.area ?? AREA, module: params.module ?? MODULE, tab: params.tab };
}

const linkClass = "text-ks-primary underline-offset-2 hover:underline focus-visible:underline";

const SECTION_LABELS = { HOME: "Home", ANALYSIS: "Analysis", CONFIG: "Config" } as const;

/** The reader's location in the module. Unlike Trail, this is always present and is not visit history. */
export function PageTrail({ current }: { current: string }) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const { area, module } = useRoute();
  const tab = tabFor(current);
  const itemId = id(search.f_item);
  const optionId = id(search.option) ?? id(search.f_option);
  const options = useFilterOptions(itemId ?? null).data?.data;
  const item = itemId ? options?.items.find((entry) => entry.item_id === itemId) : undefined;
  const option = item && optionId ? options?.options.find((entry) => entry.option_id === optionId) : undefined;
  const separator = <span aria-hidden="true">›</span>;
  return (
    <nav aria-label="Page trail" className="text-xs text-ks-grey-1">
      <ol className="flex flex-wrap items-center gap-x-1.5">
        <li className="flex items-center gap-x-1.5">
          <Link
            to="/app/$area/$module/$tab"
            params={{ area, module, tab: "margin_at_stake" } as never}
            search={((previous: Record<string, unknown>) => tabLinkSearch(previous, current)) as never}
            className={linkClass}
          >
            Sourcing Funnel
          </Link>
          {separator}
        </li>
        <li className="flex items-center gap-x-1.5">{SECTION_LABELS[tab.group]}{separator}</li>
        <li aria-current="page" className="flex items-center gap-x-1.5 text-ks-ink">
          {tab.label}{item ? separator : null}
        </li>
        {item ? (
          <li className="flex items-center gap-x-1.5">
            <ItemLink itemId={item.item_id} from={current}>{item.item_name}</ItemLink>
            {option ? separator : null}
          </li>
        ) : null}
        {item && option ? (
          <li><OptionLink optionId={option.option_id} itemId={item.item_id} from={current}>{option.option_name}</OptionLink></li>
        ) : null}
      </ol>
    </nav>
  );
}

export function ItemLink({ itemId, from, children }: { itemId: string; from: string; children: ReactNode }) {
  const { area, module } = useRoute();
  return (
    <Link
      to="/app/$area/$module/$tab"
      params={{ area, module, tab: "item_overview" } as never}
      search={((previous: Record<string, unknown>) => itemLinkSearch(previous, itemId, from)) as never}
      className={linkClass}
    >
      {children}
    </Link>
  );
}

export function OptionLink({
  optionId,
  itemId,
  from,
  children,
}: {
  optionId: string;
  itemId: string;
  from: string;
  children: ReactNode;
}) {
  const { area, module } = useRoute();
  return (
    <Link
      to="/app/$area/$module/$tab"
      params={{ area, module, tab: "option_overview" } as never}
      search={((previous: Record<string, unknown>) =>
        optionLinkSearch(previous, optionId, itemId, from)) as never}
      className={linkClass}
    >
      {children}
    </Link>
  );
}

/** A link to another page of the module that keeps the current filters; `search` may add to them. */
export function TabLink({
  tabKey,
  search,
  children,
}: {
  tabKey: string;
  search?: (previous: Record<string, unknown>) => Record<string, unknown>;
  children: ReactNode;
}) {
  const { area, module, tab } = useRoute();
  return (
    <Link
      to="/app/$area/$module/$tab"
      params={{ area, module, tab: tabKey } as never}
      search={((previous: Record<string, unknown>) => tabLinkSearch(previous, tab, search)) as never}
      className="text-ks-purple underline underline-offset-2"
    >
      {children}
    </Link>
  );
}

/** TabLink drawn as a purple chip, for a row of page buttons beside the Drive folder chip. */
export function TabChip({
  tabKey,
  search,
  children,
}: {
  tabKey: string;
  search?: (previous: Record<string, unknown>) => Record<string, unknown>;
  children: ReactNode;
}) {
  const { area, module, tab } = useRoute();
  return (
    <Badge variant="outline" asChild className="border-ks-purple text-ks-purple">
      <Link
        to="/app/$area/$module/$tab"
        params={{ area, module, tab: tabKey } as never}
        search={((previous: Record<string, unknown>) => tabLinkSearch(previous, tab, search)) as never}
      >
        {children}
      </Link>
    </Badge>
  );
}

/** The page a Back link returns to: the one named in `from`, never Option Overview itself, else `fallback`. */
export function backTarget(from: unknown, fallback?: string): string | null {
  if (isKnownTab(from) && from !== "option_overview") return from;
  return fallback ?? null;
}

export function BackLink({ fallback }: { fallback?: string } = {}) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const { area, module } = useRoute();
  // A trail has its own Back above the filter bar: this one is for a page opened without one.
  if (readTrail(search).length > 0) return null;
  const origin = backTarget(search.from, fallback);
  if (origin === null) return null;
  const target = tabFor(origin);
  return (
    <Link
      to="/app/$area/$module/$tab"
      params={{ area, module, tab: target.tabKey } as never}
      search={((previous: Record<string, unknown>) => carryFilters(previous)) as never}
      className={linkClass}
    >
      ← Back to {target.label}
    </Link>
  );
}

/**
 * The pages a reader came through by links, above the filter bar: a Back link to the last one, then
 * every step, each a link that returns there, and the page on screen. Nothing when there is no trail.
 */
export function Trail({ current }: { current: string }) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const { area, module } = useRoute();
  const steps = readTrail(search);
  if (steps.length === 0) return null;
  const last = steps.length - 1;
  const stepLink = (index: number, label: string) => (
    <Link
      to="/app/$area/$module/$tab"
      params={{ area, module, tab: steps[index].tab } as never}
      search={((previous: Record<string, unknown>) => trailBackSearch(previous, index)) as never}
      className={linkClass}
    >
      {label}
    </Link>
  );
  return (
    <nav aria-label="Trail" className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
      {stepLink(last, `← Back to ${tabFor(steps[last].tab).label}`)}
      <ol className="flex flex-wrap items-center gap-x-1.5 text-ks-grey-1">
        {steps.map((step, index) => (
          <li key={index} className="flex items-center gap-x-1.5">
            {stepLink(index, tabFor(step.tab).label)}
            <span aria-hidden="true">›</span>
          </li>
        ))}
        <li aria-current="page" className="text-ks-ink">
          {tabFor(current).label}
        </li>
      </ol>
    </nav>
  );
}
